import axios from 'axios';
import {message} from "antd";
import React from "react";
import {GENERIC_ERROR_MESSAGE} from "../constants";
import {redactSensitiveData, safeStorage} from "../errorReporting";
import {getApiBaseUrl} from "../environment";
import {getAnalyticsScopeFromToken, queueActivationTracking} from "../analytics";

const baseURL = getApiBaseUrl(window.location.hostname);

export const timezoneDataUrl = `${baseURL}/climate/timezone-data`;

const api = axios.create({
    baseURL: baseURL,
    timeout: 250000,
    headers: {
        // eslint-disable-next-line no-undef
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    // // Fetch token from store
    const token = safeStorage.get('token');

    if (token) {
        // Set token in Authorization header if token exists
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const getApiErrorData = (error) => error.response?.data || {};

export const handleApiError = (error) => {
    const status = error.response?.status;

    // check if we get a 401 error
    if (status === 401) {
        // Don't redirect for login attempts — let the login page handle its own errors
        if (error.config?.url === '/login') {
            return Promise.reject(error);
        }
        // if we get a 401 error, remove the token from storage
        safeStorage.remove('token');
        // redirect to login
        window.location.href = '/login?token_error=1';
        return Promise.reject(error);
    }

    const errorContext = {
        request_path: error.config?.url || null,
        request_method: error.config?.method?.toUpperCase() || null,
        request_body: redactSensitiveData(error.config?.data || null),
    };

    message.info(<span>
        {GENERIC_ERROR_MESSAGE}{' '}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a onClick={() => window.dispatchEvent(new CustomEvent('api-error', {detail: errorContext}))}>
            Aan Stef melden
        </a>
    </span>, 8);

    return Promise.reject(error);
};

api.interceptors.response.use(
    (response) => {
        return response.data;
    },
    handleApiError
);

const gardenApi = {
    reportedTranslations: [],
    register(email, name, password, company, utm_source, utm_medium, utm_campaign, country, language, measurements, supplier_ids, hardiness_zone, postal_code, plan_id) {
        return api.post('/register', {email, password, name, company, utm_source, utm_medium, utm_campaign, country, language, measurements, supplier_ids, hardiness_zone, postal_code, plan_id})
    },
    requestPasswordReset(email) {
        return api.post('/request-password-reset', {email})
    },
    resetPassword(user_id, token, password) {
        return api.post(`/reset-password`, {user_id, token, password})
    },
    login(email, password) {
        return api.post('/login', {email, password})
    },
    gardens() {
        return api.get('/gardens');
    },
    deleteGarden(id) {
        return api.delete(`/gardens/${id}`)
    },
    updateGarden(id, data) {
        return api.put(`/gardens/${id}`, data)
    },
    createGarden(data) {
        return api.post('/gardens', data)
    },
    garden(id) {
        return api.get(`/gardens/${id}`)
    },
    beds(id) {
        return api.get(`/gardens/${id}/beds`)
    },
    bed(garden_id, bed_id) {
        return api.get(`/gardens/${garden_id}/beds/${bed_id}`)
    },
    saveBedOrder(garden_id, bed_ids) {
        return api.put(`/gardens/${garden_id}/set-bed-order`, {bed_ids})
    },
    savePlantingOrder(garden_id, bed_id, planting_ids) {
        return api.put(`/gardens/${garden_id}/beds/${bed_id}/set-planting-order`, {planting_ids})
    },
    trackCropClick(crop_id) {
        return api.get(`/crops/${crop_id}/track-click`)
    },
    planting(garden_id, bed_id, planting_id) {
        return api.get(`/gardens/${garden_id}/beds/${bed_id}/plantings/${planting_id}`)
    },
    addBed(garden_id, bed) {
        return api.post(`/gardens/${garden_id}/beds`, bed)
    },
    updateBed(garden_id, bed_id, bed) {
        return api.put(`/gardens/${garden_id}/beds/${bed_id}`, bed)
    },
    removeBed(garden_id, bed_id) {
        return api.delete(`/gardens/${garden_id}/beds/${bed_id}`)
    },
    setBedPositions(garden_id, beds) {
        return api.put(`/gardens/${garden_id}/bed-positions`, beds)
    },
    cropTypes() {
        return api.get('/crop-types')
    },
    crops({
              search = null,
              sort = "id_desc",
              stock_status = "all_stock",
              plant_in = [],
              sow_in = [],
              harvest_in = [],
              crop_type_id = null
          }) {
        let url = '/crops'
        let queries = []
        if (search) {
            queries.push(`search=${search}`)
        }
        if (sort) {
            queries.push(`sort=${sort}`)
        }
        if (stock_status) {
            queries.push(`stock_status=${stock_status}`)
        }
        if (plant_in.length > 0) {
            queries.push(`plant_in=${plant_in.join(',')}`)
        }
        if (sow_in.length > 0) {
            queries.push(`sow_in=${sow_in.join(',')}`)
        }
        if (harvest_in.length > 0) {
            queries.push(`harvest_in=${harvest_in.join(',')}`)
        }
        if (crop_type_id) {
            queries.push(`crop_type_id=${crop_type_id}`)
        }

        if (queries.length > 0) {
            url += '?' + queries.join('&')
        }

        return api.get(url)
    },
    addPlanting(planting) {
        const dateFields = ['sow_at', 'prick_at', 'transplant_at', 'harvest_at', 'harvest_until']
        dateFields.forEach(field => {
            if (planting[field] && planting[field].format) {
                planting[field] = planting[field].format('YYYY-MM-DD')
            }
            if (planting[field] === 'Invalid Date') {
                planting[field] = null
            }
        })
        const plantingRequest = api.post('/plantings', planting)
        try {
            queueActivationTracking(plantingRequest, getAnalyticsScopeFromToken(safeStorage.get('token')))
        } catch {
            // A successful planting must not depend on analytics.
        }
        return plantingRequest
    },
    removePlanting(id) {
        return api.delete('/plantings/' + id)
    },
    companyCrops(company_id) {
        return api.get('/company/' + company_id + '/crops')
    },
    updatePlanting(id, planting) {
        const dateFields = ['sow_at', 'prick_at', 'transplant_at', 'harvest_at', 'harvest_until']
        dateFields.forEach(field => {
            if (planting[field] && planting[field].format) {
                planting[field] = planting[field].format('YYYY-MM-DD')
            }
            if (planting[field] === 'Invalid Date') {
                planting[field] = null
            }
        })
        return api.put('/plantings/' + id, planting)
    },
    getCropPlan() {
        return api.get('/cropplan')
    },
    search(query) {
        return api.post('/search', {query})
    },
    getCropInfo(cropId) {
        return api.post('/crops/get-info', { crop_id: cropId })
    },
    getCrop(id) {
        return api.get('/crops/' + id)
    },
    deleteCrop(id) {
        return api.delete('/crops/' + id)
    },
    updateCrop(id, crop) {
        return api.put('/crops/' + id, crop)
    },
    createCrop(crop) {
        return api.post('/crops', crop)
    },
    clearCropStock() {
        return api.post('/crops/clear-stock')
    },
    autoAssignCropIcon(cropId) {
        return api.post(`/crops/${cropId}/auto-icon`)
    },
    getAllIcons() {
        return api.get('/icons')
    },
    searchFreepikIconsUser(term) {
        return api.get(`/icons/search/freepik?term=${encodeURIComponent(term)}`)
    },
    setCropIcon(cropId, iconId) {
        return api.put(`/crops/${cropId}/icon?icon_id=${iconId}`)
    },
    createIconFromFreepikUser(name, thumbnailUrl) {
        return api.post('/icons/from-freepik', { name: name, thumbnail_url: thumbnailUrl })
    },
    // Products API functions
    products({
                     search = null,
                     sort = "popularity",
                     plant_in = [],
                     sow_in = [],
                     harvest_in = [],
                     supplier_id = null
                 }) {
        let url = '/products'
        let queries = []
        if (search) {
            queries.push(`search=${search}`)
        }
        if (sort) {
            queries.push(`sort=${sort}`)
        }
        if (plant_in.length > 0) {
            queries.push(`plant_in=${plant_in.join(',')}`)
        }
        if (sow_in.length > 0) {
            queries.push(`sow_in=${sow_in.join(',')}`)
        }
        if (harvest_in.length > 0) {
            queries.push(`harvest_in=${harvest_in.join(',')}`)
        }
        if (supplier_id) {
            queries.push(`supplier_id=${supplier_id}`)
        }

        if (queries.length > 0) {
            url += '?' + queries.join('&')
        }

        return api.get(url)
    },
    getProduct(id) {
        return api.get('/products/' + id)
    },
    getProductSuppliers() {
        return api.get('/products/meta/suppliers')
    },
    updateProduct(id, data) {
        return api.put('/admin/products/' + id, data)
    },
    uploadToCdn(file) {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/cdn/upload", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
    },
    cdnResize(src, width, height) {
        return api.post(`/cdn/resize`, {
            src,
            width,
            height

        })
    },
    logUpdate(update) {
        return api.post(`/updates`, update)
    },
    getPlantingUpdates(id) {
        return api.get(`/plantings/${id}/updates`)
    },
    deleteUpdate(update_id) {
        return api.delete(`/updates/${update_id}`)
    },
    getAllUpdates(id) {
        return api.get(`/updates`)
    },
    getUpdatesFeed(page = 1, pageSize = 12, mine = false) {
        return api.get(`/updates/feed?page=${page}&page_size=${pageSize}&mine=${mine}`)
    },
    sendUpdates(update_ids, description, subject) {
        return api.post(`/updates/send`, {update_ids, description, subject})
    },
    isLoggedIn() {
        return safeStorage.get('token')
    },

    // ADMIN ROUTES
    isAdmin() {
        const jwt = safeStorage.get('token');
        // Parse the JWT token and check if it has the is_admin flag set
        if (jwt) {
            try {
                // Decode the payload
                const base64Url = jwt.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                const payload = JSON.parse(jsonPayload);
                return payload.is_admin;
            } catch (e) {
                return false;
            }
        }
        return false;
    },
    impersonate(user_id) {
        return api.get(`/admin/impersonate/${user_id}`)
    },
    getAdminStats(user_id) {
        return api.get(`/admin/stats`)
    },
    clearCrawlerQueue() {
        return api.delete(`/admin/crawler-queue`)
    },
    startAllSupplierCrawls() {
        return api.post(`/admin/suppliers/start-crawl-all`)
    },
    getCompanyGrowthStats(start_date = null, end_date = null) {
        let params = []
        if (start_date) {
            params.push(`start_date=${start_date}`)
        }
        if (end_date) {
            params.push(`end_date=${end_date}`)
        }
        let url = '/admin/companies/stats/'
        if (params.length > 0) {
            url += '?' + params.join('&')
        }
        return api.get(url)
    },
    getRevenueGrowthStats(start_date = null, end_date = null) {
        let params = []
        if (start_date) {
            params.push(`start_date=${start_date}`)
        }
        if (end_date) {
            params.push(`end_date=${end_date}`)
        }
        let url = '/admin/revenue/stats/'
        if (params.length > 0) {
            url += '?' + params.join('&')
        }
        return api.get(url)
    },
    getUtmSourceStats(start_date = null, end_date = null, only_new_customers = false) {
        let params = []
        if (start_date) {
            params.push(`start_date=${start_date}`)
        }
        if (end_date) {
            params.push(`end_date=${end_date}`)
        }
        if (only_new_customers) {
            params.push(`only_new_customers=true`)
        }
        let url = '/admin/utm-source-stats'
        if (params.length > 0) {
            url += '?' + params.join('&')
        }
        return api.get(url)
    },
    getSignupsPerDayStats(start_date = null, end_date = null) {
        let params = []
        if (start_date) {
            params.push(`start_date=${start_date}`)
        }
        if (end_date) {
            params.push(`end_date=${end_date}`)
        }
        let url = '/admin/signups/stats/'
        if (params.length > 0) {
            url += '?' + params.join('&')
        }
        return api.get(url)
    },
    getUserCountByLanguage() {
        return api.get('/admin/user-count-by-language')
    },
    searchUser(email) {
        return api.post(`/admin/users`, {
            email
        })
    },
    getAdminPlans() {
        return api.get(`/admin/plans`)
    },
    updateCompanyPlan(companyId, data) {
        return api.put(`/admin/companies/${companyId}/plan`, data)
    },
    adminSendPasswordReset(userId) {
        return api.post(`/admin/users/${userId}/send-password-reset`)
    },
    getPublicProfiles(email) {
        return api.get(`/profiles`)
    },
    get_all_blog_posts() {
        return api.get(`/admin/blog-posts`)
    },
    get_blog_url(slug) {
        if (baseURL === "https://api.oogst.app") {
            return `https://www.oogst.app/blog/${slug}`
        }
        return `http://localhost:9002/blog/${slug}`
    },
    getBlogTags(language) {
        return api.get(`/blogs/tags?language=${language}`)
    },
    generate_blog_posts_from_search_terms(data) {
        return api.post(`/admin/blog-posts/generate-from-search-terms`, data)
    },
    addTeamMember(name, email) {
        return api.post(`/team`, {name, email})
    },
    updateTeamMember(id, values) {
        return api.put(`/team/${id}`, values)
    },
    getTeamMembers() {
        return api.get(`/team`)
    },
    deactivateTeamMember(id) {
        return api.delete(`/team/${id}`)
    },
    getInvite(id, token) {
        return api.get(`/team/invite/${id}/${token}`)
    },
    acceptInvite(id, token, password) {
        return api.post(`/team/accept/${id}/${token}`, {password})
    },
    get_plan() {
        return api.get(`/my/plan`)
    },
    getMyCompany() {
        return api.get(`/my/company`)
    },
    updateCompany(company) {
        return api.put(`/my/company`, company)
    },
    getClimateSettings() {
        return api.get('/my/company/climate-settings')
    },
    updateClimateSettings(settings) {
        return api.put('/my/company/climate-settings', settings)
    },
    upgradePlan(plan_id, discount_code) {
        return api.post(`/payment/upgrade`, {plan_id, discount_code})
    },
    getAllPlans(market = 'EU') {
        return api.get(`/plans?market=${encodeURIComponent(market)}`)
    },
    getDiscountCode(code) {
        return api.get(`/discount/${code}`)
    },
    getPaymentMethods() {
        return api.get(`/payment-methods`)
    },
    getPaymentAnalytics(paymentId) {
        return api.get(`/payment/${encodeURIComponent(paymentId)}/analytics`)
    },
    getSubscriptionStatus() {
        return api.get(`/subscription/status`)
    },
    cancelSubscription() {
        return api.post(`/subscription/cancel`)
    },
    reactivateSubscription() {
        return api.post(`/subscription/reactivate`)
    },
    getMe() {
        return api.get(`/me`)
    },
    getCompanyBySlug(slug) {
        return api.get(`/company/slug/${slug}`)
    },
    subscribeToCompany(company_id, name, email) {
        return api.post(`/company/${company_id}/subscribe`, {name, email})
    },
    addQuestion(question_text) {
        return api.post(`/questions`, {question_text})
    },
    getQuestions() {
        return api.get(`/questions`)
    },
    answerQuestion(id, answer_text, loom_video) {
        return api.post(`/admin/questions/${id}`, {answer_text, loom_video})
    },
    markQuestionAsAnswered(id, answer_text, loom_video) {
        return api.put(`/admin/questions/${id}`)
    },
    getAllQuestions() {
        return api.get(`/admin/questions`)
    },
    getQuestionThread(user_id) {
        return api.get(`/admin/questions/thread/${user_id}`)
    },
    getAllTranslations() {
        return api.get(`/translations`)
    },
    addMissingTranslation(key) {

        if (this.reportedTranslations.includes(key)) {
            return
        }

        this.reportedTranslations.push(key)
        return api.post(`/translations`, {key})
    },
    // Admin translation management
    getAllTranslationsForAdmin() {
        return api.get('/admin/translations')
    },
    updateTranslation(key, translations) {
        return api.put(`/admin/translations/${encodeURIComponent(key)}`, translations)
    },
    deleteTranslation(key) {
        return api.delete(`/admin/translations/${encodeURIComponent(key)}`)
    },
    getTasks({
                 garden_id = null,
                 user_id = null,
                 task_type = null,
                 is_completed = null,
                 start_date = null,
                 end_date = null,
                 planting_id = null
             }) {

        let url = `/tasks`
        let queries = []

        if (garden_id) {
            queries.push(`garden_id=${garden_id}`)
        }
        if (user_id) {
            queries.push(`user_id=${user_id}`)
        }
        if (task_type) {
            queries.push(`task_type=${task_type}`)
        }
        if (is_completed !== null) {
            queries.push(`is_completed=${is_completed}`)
        }
        if (start_date) {
            queries.push(`start_date=${start_date}`)
        }
        if (end_date) {
            queries.push(`end_date=${end_date}`)
        }
        if (planting_id) {
            queries.push(`planting_id=${planting_id}`)
        }

        if (queries.length > 0) {
            url += '?' + queries.join('&')
        }

        return api.get(url)
    },
    toggleTask(taskId, is_completed) {
        return api.post(`/tasks/${taskId}/toggle`, {is_completed})
    },
    updateTask(taskId, data) {
        return api.put(`/tasks/${taskId}`, data)
    },
    snoozeTask(taskId, days) {
        return api.post(`/tasks/${taskId}/snooze`, {days})
    },
    createTask(data) {
        return api.post(`/tasks`, data)
    },
    getOffers() {
        return api.get(`/offers`)
    },
    createOffer(data) {
        return api.post(`/offers`, data)
    },
    updateOffer(id, data) {
        return api.put(`/offers/${id}`, data)
    },
    getCompanyOffers(company_id) {
        return api.get(`/company/${company_id}/offers`)
    },
    getOffersByIds(ids) {
        return api.post('/offers/ids', ids)
    },
    deleteOffer(id) {
        return api.delete(`/offers/${id}`)
    },
    archiveOffer(id) {
        return api.post(`/offers/${id}/archive`)
    },
    getOffersByPlanting(planting_id) {
        return api.get(`/plantings/${planting_id}/offers`)
    },
    createOrder(order) {
        return api.post(`/orders`, order)
    },
    getOrder(order_id, secret) {
        return api.get(`/orders/${order_id}/${secret}`)
    },
    updateCompanyStore(company_id, store) {
        console.log(store)
        return api.put(`/company/${company_id}/store`, store)
    },
    createPickUpSlot(company_id, slot) {
        return api.post(`/company/${company_id}/pickup-slots`, slot)
    },
    calculateDeliveryCosts(company_id, lat, lng) {
        return api.post(`/company/${company_id}/delivery-costs`, {
            lat,
            lng
        })
    },
    getPickUpSlots(company_id) {
        return api.get(`/company/${company_id}/pickup-slots`)
    },
    getCompanyOrders(){
        return api.get(`/orders`)
    },
    getOrderStats() {
        return api.get(`/order-stats`)
    },
    getSalesOverview(dateRange = 'all_time') {
        return api.get(`/sales-overview?date_range=${dateRange}`)
    },
    messageSeller(order_id, secret, message) {
        return api.post(`/orders/${order_id}/${secret}/message-seller`, {message})
    },
    messageBuyer(order_id, secret, message) {
        return api.post(`/orders/${order_id}/${secret}/message-buyer`, {message})
    },
    subscribeToAlerts(data) {
        return api.post('/subscribe', data)
    },
    getCompanySubscribers() {
        return api.get('/my/company/subscribers')
    },
    updateOrderStatus(order_id, status) {
        return api.put(`/orders/${order_id}`, {status});
    },
    getBlogBySlug(slug) {
        return api.get(`/blog/${slug}`)
    },
    getAllBlogs(language = null, limit =3) {

        let params = []
        if (language) {
            params.push(`language=${language}`)
        }
        if (limit) {
            params.push(`limit=${limit}`)
        }
        return api.get(`/blogs?` + params.join('&'))
    },
    getBlogsByTag(tag, language = null, limit = 50) {
        let params = []
        if (language) {
            params.push(`language=${language}`)
        }
        if (limit) {
            params.push(`limit=${limit}`)
        }
        return api.get(`/blogs/tag/${tag}?` + params.join('&'))
    },
    getAffiliateInfo() {
        return api.get(`/my/affiliate`)
    },
    changePassword(currentPassword, newPassword) {
        return api.post(`/my/change-password`, { current_password: currentPassword, new_password: newPassword })
    },
    deleteAccount() {
        return api.delete(`/my/account`)
    },

    // Supplier Management (Admin)
    getSuppliers() {
        return api.get('/admin/suppliers')
    },
    getPublicSuppliers(language) {
        return api.get(`/public/suppliers?language=${language}`)
    },
    getSupplier(id) {
        return api.get(`/admin/suppliers/${id}`)
    },
    createSupplier(data) {
        return api.post('/admin/suppliers', data)
    },
    updateSupplier(id, data) {
        return api.put(`/admin/suppliers/${id}`, data)
    },
    deleteSupplier(id) {
        return api.delete(`/admin/suppliers/${id}`)
    },
    startSupplierCrawl(id) {
        return api.post(`/admin/suppliers/${id}/start-crawl`)
    },

    // Ad Spend Management
    getAdSpends() {
        return api.get('/admin/ad-spends')
    },
    getAdSpend(id) {
        return api.get(`/admin/ad-spends/${id}`)
    },
    createAdSpend(data) {
        return api.post('/admin/ad-spends', data)
    },
    updateAdSpend(id, data) {
        return api.put(`/admin/ad-spends/${id}`, data)
    },
    deleteAdSpend(id) {
        return api.delete(`/admin/ad-spends/${id}`)
    },

    // Manual Order Creation
    createManualOrder(orderData) {
        return api.post('/orders/manual', orderData)
    },
    getOrderPaymentUrl(order_id, secret) {
        return api.get(`/orders/${order_id}/${secret}/payment-url`)
    },

    // Crop Task Templates
    getCropTasks(cropId) {
        return api.get(`/crops/${cropId}/tasks`)
    },
    createCropTask(cropId, task) {
        return api.post(`/crops/${cropId}/tasks`, task)
    },
    updateCropTask(cropId, taskId, task) {
        return api.put(`/crops/${cropId}/tasks/${taskId}`, task)
    },
    deleteCropTask(cropId, taskId) {
        return api.delete(`/crops/${cropId}/tasks/${taskId}`)
    },

    // Task Day Preferences
    getTaskDayPreferences() {
        return api.get('/tasks/day-preferences')
    },
    updateTaskDayPreferences(preferences) {
        return api.put('/tasks/day-preferences', { preferences })
    },

    // Icon Management (Admin)
    getIcons() {
        return api.get('/admin/icons')
    },
    getIcon(id) {
        return api.get(`/admin/icons/${id}`)
    },
    createIcon(data) {
        return api.post('/admin/icons', data)
    },
    updateIcon(id, data) {
        return api.put(`/admin/icons/${id}`, data)
    },
    deleteIcon(id) {
        return api.delete(`/admin/icons/${id}`)
    },
    searchFreepikIcons(term) {
        return api.get(`/admin/icons/search/freepik?term=${encodeURIComponent(term)}`)
    },
    createIconFromFreepik(name, thumbnailUrl) {
        return api.post('/admin/icons/from-freepik', { name: name, thumbnail_url: thumbnailUrl })
    },

    // Help Videos
    getHelpVideos() {
        return api.get('/admin/help-videos')
    },
    getHelpVideo(id) {
        return api.get(`/admin/help-videos/${id}`)
    },
    createHelpVideo(data) {
        return api.post('/admin/help-videos', data)
    },
    updateHelpVideo(id, data) {
        return api.put(`/admin/help-videos/${id}`, data)
    },
    deleteHelpVideo(id) {
        return api.delete(`/admin/help-videos/${id}`)
    },
    getHelpVideoForPage(pathname, language = 'en') {
        return api.get(`/help-videos?pathname=${pathname}&language=${language}`)
    },
    submitFeedback(data) {
        return api.post('/feedback', data)
    },

    // Harvest Goals
    getHarvestGoals(year) {
        return api.get(`/harvest-goals?year=${year}`)
    },
    createHarvestGoal(data) {
        return api.post('/harvest-goals', data)
    },
    updateHarvestGoal(id, data) {
        return api.put(`/harvest-goals/${id}`, data)
    },
    deleteHarvestGoal(id) {
        return api.delete(`/harvest-goals/${id}`)
    },
    generatePlan(data) {
        return api.post('/harvest-goals/generate-plan', data)
    },

    // Chart Annotations (Admin)
    getAnnotations(start_date = null, end_date = null) {
        let params = []
        if (start_date) params.push(`start_date=${start_date}`)
        if (end_date) params.push(`end_date=${end_date}`)
        let url = '/admin/annotations/'
        if (params.length > 0) url += '?' + params.join('&')
        return api.get(url)
    },
    createAnnotation(data) {
        return api.post('/admin/annotations/', data)
    },
    updateAnnotation(id, data) {
        return api.put(`/admin/annotations/${id}/`, data)
    },
    deleteAnnotation(id) {
        return api.delete(`/admin/annotations/${id}/`)
    },

    // Affiliate Management (Admin)
    getAffiliates() {
        return api.get('/admin/affiliates')
    },
    createAffiliate(data) {
        return api.post('/admin/affiliates', data)
    },

    // Affiliate Requests
    submitAffiliateRequest(data) {
        return api.post('/affiliate-request', data)
    },
    getAffiliateRequests() {
        return api.get('/admin/affiliate-requests')
    },
    approveAffiliateRequest(id, { slug, discount_code } = {}) {
        return api.post(`/admin/affiliate-requests/${id}/approve`, { slug, discount_code })
    },
    denyAffiliateRequest(id, denial_reason) {
        return api.post(`/admin/affiliate-requests/${id}/deny`, { denial_reason })
    },

    // Discount Code Management (Admin)
    getDiscountCodes() {
        return api.get('/admin/discount-codes')
    },
    createDiscountCode(data) {
        return api.post('/admin/discount-codes', data)
    },
    updateDiscountCode(id, data) {
        return api.put(`/admin/discount-codes/${id}`, data)
    },
    deleteDiscountCode(id) {
        return api.delete(`/admin/discount-codes/${id}`)
    }
}

export default gardenApi;
